/* Iron & Acre — Meals Made Easy
   Native Shopify multi-item cart controller. No bundle products or discounts. */
(function () {
  'use strict';

  var ROOT_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function toNumber(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function bool(value) {
    return value === true || value === 'true';
  }

  function formatMoney(cents, currency) {
    var value = toNumber(cents, 0) / 100;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
        maximumFractionDigits: value % 1 === 0 ? 0 : 2
      }).format(value);
    } catch (error) {
      return '$' + value.toFixed(value % 1 === 0 ? 0 : 2);
    }
  }

  function dispatchAnalytics(name, detail) {
    var payload = Object.assign({ event: name }, detail || {});
    document.dispatchEvent(new CustomEvent(name, { detail: payload }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
  }

  function cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function cartSections(drawer) {
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer.getSectionsToRender().map(function (section) { return section.id; });
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function publishCartUpdate(variantId, cartData) {
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      return Promise.resolve(window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'ia-meals-made-easy',
        productVariantId: String(variantId || ''),
        variantId: String(variantId || ''),
        cartData: cartData
      }));
    }
    return Promise.resolve();
  }

  function parseJsonResponse(response) {
    return response.text().then(function (text) {
      var data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (error) {
        data = { description: text || 'The cart returned an unreadable response.' };
      }
      return { response: response, data: data };
    });
  }

  function fetchCart() {
    return fetch(ROOT_URL + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to read the current cart.');
        return response.json();
      });
  }

  function privateProperty(item, key) {
    return item && item.properties ? item.properties[key] || '' : '';
  }

  function lineSignature(item) {
    var sellingPlanId = '';
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      sellingPlanId = String(item.selling_plan_allocation.selling_plan.id || '');
    } else if (item.selling_plan) {
      sellingPlanId = String(item.selling_plan);
    }
    return [
      String(item.variant_id || item.id || ''),
      sellingPlanId,
      privateProperty(item, '_ia_meal_bundle_id'),
      privateProperty(item, '_ia_meal_bundle_role')
    ].join('|');
  }

  function cartQuantities(cart) {
    var quantities = {};
    (cart && cart.items ? cart.items : []).forEach(function (item) {
      var key = lineSignature(item);
      quantities[key] = (quantities[key] || 0) + toNumber(item.quantity, 0);
    });
    return quantities;
  }

  function mealCartLines(cart) {
    var lines = {};
    (cart && cart.items ? cart.items : []).forEach(function (item) {
      var bundleId = privateProperty(item, '_ia_meal_bundle_id');
      if (!bundleId) return;
      var key = lineSignature(item);
      var quantity = toNumber(item.quantity, 0);
      var unitPrice = toNumber(item.final_price || item.price, 0);
      if (!lines[key]) {
        lines[key] = {
          bundle_id: bundleId,
          bundle_name: privateProperty(item, '_ia_meal_bundle_name'),
          bundle_role: privateProperty(item, '_ia_meal_bundle_role'),
          variant_id: String(item.variant_id || item.id || ''),
          quantity: 0,
          unit_price_cents: unitPrice
        };
      }
      lines[key].quantity += quantity;
    });
    return lines;
  }

  function requestSignature(item) {
    return [
      String(item.id || ''),
      item.selling_plan ? String(item.selling_plan) : '',
      item.properties ? String(item.properties._ia_meal_bundle_id || '') : '',
      item.properties ? String(item.properties._ia_meal_bundle_role || '') : ''
    ].join('|');
  }

  function refreshDrawerSections(drawer, sections) {
    if (!drawer || typeof drawer.renderContents !== 'function') return Promise.resolve(null);
    var path = window.location.pathname || '/';
    var separator = path.indexOf('?') === -1 ? '?' : '&';
    var url = path + separator + 'sections=' + encodeURIComponent(sections.join(','));
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to refresh cart sections.');
        return response.json();
      })
      .then(function (sectionData) {
        var contents = { sections: sectionData };
        drawer.classList.remove('is-empty');
        drawer.renderContents(contents);
        return contents;
      });
  }

  function selectedOption(select) {
    return select && select.options ? select.options[select.selectedIndex] : null;
  }

  function MealController(root) {
    this.root = root;
    this.bundleId = root.dataset.bundleId || '';
    this.bundleName = root.dataset.bundleName || '';
    this.placement = root.dataset.placement || 'detail';
    this.currency = root.dataset.currency || 'USD';
    this.ctaLabel = root.dataset.ctaLabel || 'Add Selected Groceries';
    this.allowPartial = bool(root.dataset.allowPartial);
    this.enableSubscription = bool(root.dataset.enableSubscription);
    this.items = Array.prototype.slice.call(root.querySelectorAll('[data-mme-item]'));
    this.totalElement = root.querySelector('[data-mme-total]');
    this.countElement = root.querySelector('[data-mme-count]');
    this.primaryButton = root.querySelector('[data-mme-add="complete"]');
    this.partialButton = root.querySelector('[data-mme-add="available"]');
    this.notice = root.querySelector('[data-mme-notice]');
    this.liveRegion = root.querySelector('[data-mme-live]');
    this.subscriptionWrap = root.querySelector('[data-mme-subscription-wrap]');
    this.subscriptionInput = root.querySelector('[data-mme-subscription]');
    this.pending = false;
    this.viewTracked = false;
    this.lastTotal = 0;
    this.bind();
    this.update('initial');
    this.observeView();
  }

  MealController.prototype.bind = function () {
    var controller = this;
    this.root.addEventListener('change', function (event) {
      var control = event.target;
      var item = control.closest ? control.closest('[data-mme-item]') : null;
      var before = controller.lastTotal;
      controller.update('change');
      if (control.matches('[data-mme-choice], [data-mme-optional], [data-mme-variant-select], [data-mme-subscription]')) {
        var variant = item ? controller.variantFor(item) : null;
        dispatchAnalytics('ia_meal_bundle_selection_changed', {
          bundle_id: controller.bundleId,
          bundle_name: controller.bundleName,
          placement: controller.placement,
          role: item ? item.dataset.role || '' : 'Subscription',
          action: control.matches('[data-mme-subscription]') ? (control.checked ? 'subscription_selected' : 'one_time_selected') : 'selection_changed',
          variant_id: variant ? variant.id : '',
          prior_value_cents: before,
          current_value_cents: controller.lastTotal
        });
      }
    });

    if (this.primaryButton) {
      this.primaryButton.addEventListener('click', function () { controller.add('complete'); });
    }
    if (this.partialButton) {
      this.partialButton.addEventListener('click', function () { controller.add('available'); });
    }
  };

  MealController.prototype.observeView = function () {
    var controller = this;
    var track = function () {
      if (controller.viewTracked) return;
      controller.viewTracked = true;
      var state = controller.selectionState();
      dispatchAnalytics('ia_meal_bundle_view', {
        bundle_id: controller.bundleId,
        bundle_name: controller.bundleName,
        placement: controller.placement,
        selected_value_cents: state.total,
        item_count: state.selected.length
      });
    };
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) {
          track();
          observer.disconnect();
        }
      }, { threshold: 0.2 });
      observer.observe(this.root);
    } else {
      track();
    }
  };

  MealController.prototype.isSelected = function (item) {
    var behavior = item.dataset.behavior || 'required';
    if (behavior === 'required') return true;
    if (behavior === 'required_choice') {
      var radio = item.querySelector('[data-mme-choice]');
      return Boolean(radio && radio.checked);
    }
    if (behavior === 'optional') {
      var checkbox = item.querySelector('[data-mme-optional]');
      return Boolean(checkbox && checkbox.checked);
    }
    return false;
  };

  MealController.prototype.variantFor = function (item) {
    var select = item.querySelector('[data-mme-variant-select]');
    var option = selectedOption(select);
    var source = option || item;
    return {
      id: toNumber(source.dataset.variantId || (option ? option.value : ''), 0),
      title: source.dataset.variantTitle || '',
      price: toNumber(source.dataset.price, 0),
      available: bool(source.dataset.available),
      sellingPlanId: toNumber(source.dataset.sellingPlanId, 0),
      sellingPlanPrice: toNumber(source.dataset.sellingPlanPrice, 0)
    };
  };

  MealController.prototype.selectionState = function () {
    var controller = this;
    var selected = [];
    var missing = [];
    var requiredGroups = {};

    this.items.forEach(function (item) {
      var behavior = item.dataset.behavior || 'required';
      var group = item.dataset.choiceGroup || '';
      var variant = controller.variantFor(item);
      var isSelected = controller.isSelected(item);

      if (behavior === 'required_choice' && group) {
        requiredGroups[group] = requiredGroups[group] || { selected: false, role: item.dataset.role || 'Required choice' };
        if (isSelected) requiredGroups[group].selected = true;
      }

      if (behavior === 'optional' && isSelected && !variant.available) {
        var optionalInput = item.querySelector('[data-mme-optional]');
        if (optionalInput) optionalInput.checked = false;
        isSelected = false;
      }

      item.classList.toggle('is-selected', isSelected);
      item.classList.toggle('is-unavailable', !variant.available);
      item.setAttribute('aria-disabled', !variant.available ? 'true' : 'false');

      var price = variant.price;
      if (controller.subscriptionInput && controller.subscriptionInput.checked && variant.sellingPlanId && variant.sellingPlanPrice) {
        price = variant.sellingPlanPrice;
      }
      var priceElement = item.querySelector('[data-mme-item-price]');
      if (priceElement) priceElement.textContent = formatMoney(price, controller.currency);

      if (isSelected) {
        selected.push({ item: item, variant: variant, price: price });
        if (behavior !== 'optional' && !variant.available) {
          missing.push(item.dataset.role || item.dataset.productTitle || 'Required item');
        }
      }
    });

    Object.keys(requiredGroups).forEach(function (group) {
      if (!requiredGroups[group].selected) missing.push(requiredGroups[group].role);
    });

    var subscriptionEligible = selected.length > 0 && selected.every(function (selection) {
      return selection.variant.sellingPlanId && selection.variant.sellingPlanPrice;
    });
    if (this.subscriptionWrap) {
      this.subscriptionWrap.classList.toggle('is-visible', this.enableSubscription && subscriptionEligible);
    }
    if ((!this.enableSubscription || !subscriptionEligible) && this.subscriptionInput && this.subscriptionInput.checked) {
      this.subscriptionInput.checked = false;
      selected.forEach(function (selection) { selection.price = selection.variant.price; });
    }

    var total = selected.reduce(function (sum, selection) {
      return sum + selection.price * toNumber(selection.item.dataset.quantity, 1);
    }, 0);

    return {
      selected: selected,
      missing: Array.from(new Set(missing)),
      total: total,
      availableSelected: selected.filter(function (selection) { return selection.variant.available; })
    };
  };

  MealController.prototype.update = function () {
    var state = this.selectionState();
    this.lastTotal = state.total;
    if (this.totalElement) this.totalElement.textContent = formatMoney(state.total, this.currency);
    if (this.countElement) {
      this.countElement.textContent = state.selected.length + (state.selected.length === 1 ? ' selected grocery' : ' selected groceries');
    }

    var hasMissing = state.missing.length > 0;
    if (this.notice) {
      if (hasMissing) {
        this.notice.classList.add('is-visible');
        this.notice.dataset.status = 'availability';
        this.notice.textContent = 'This meal is missing: ' + state.missing.join(', ') + '. Choose an available alternative where one is shown.';
      } else if (!this.notice.dataset.status || this.notice.dataset.status === 'availability') {
        this.notice.classList.remove('is-visible');
        this.notice.textContent = '';
        delete this.notice.dataset.status;
      }
    }

    if (this.primaryButton) {
      this.primaryButton.disabled = this.pending || hasMissing || state.selected.length === 0;
      this.primaryButton.textContent = this.pending ? 'Adding…' : this.ctaLabel + ' · ' + formatMoney(state.total, this.currency);
    }
    if (this.partialButton) {
      var showPartial = this.allowPartial && hasMissing && state.availableSelected.length > 0;
      this.partialButton.classList.toggle('is-visible', showPartial);
      this.partialButton.disabled = this.pending || !showPartial;
      this.partialButton.textContent = 'Add ' + state.availableSelected.length + ' Available ' + (state.availableSelected.length === 1 ? 'Item' : 'Items');
    }
    return state;
  };

  MealController.prototype.requestItems = function (mode) {
    var controller = this;
    var state = this.selectionState();
    var selections = mode === 'available' ? state.availableSelected : state.selected;
    return selections.filter(function (selection) {
      return mode === 'available' ? selection.variant.available : true;
    }).map(function (selection) {
      var item = {
        id: selection.variant.id,
        quantity: toNumber(selection.item.dataset.quantity, 1),
        properties: {
          _ia_meal_bundle_id: controller.bundleId,
          _ia_meal_bundle_name: controller.bundleName,
          _ia_meal_bundle_role: selection.item.dataset.role || selection.item.dataset.productTitle || 'Item'
        }
      };
      if (controller.subscriptionInput && controller.subscriptionInput.checked && selection.variant.sellingPlanId) {
        item.selling_plan = selection.variant.sellingPlanId;
      }
      return item;
    });
  };

  MealController.prototype.setPending = function (pending) {
    this.pending = pending;
    this.root.classList.toggle('is-pending', pending);
    this.update();
  };

  MealController.prototype.announce = function (message) {
    if (this.liveRegion) this.liveRegion.textContent = message;
  };

  MealController.prototype.showNotice = function (message, isError) {
    if (!this.notice) return;
    this.notice.textContent = message;
    this.notice.classList.add('is-visible');
    this.notice.dataset.status = isError ? 'error' : 'partial';
  };

  MealController.prototype.renderSuccessfulCart = function (data, drawer, sections, firstVariantId) {
    var controller = this;
    return publishCartUpdate(firstVariantId, data).then(function () {
      if (drawer && data.sections && typeof drawer.renderContents === 'function') {
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
        return data;
      }
      return refreshDrawerSections(drawer, sections).then(function () { return data; });
    }).catch(function () {
      return refreshDrawerSections(drawer, sections).then(function () { return data; });
    });
  };

  MealController.prototype.reconcile = function (beforeCart, afterCart, requestItems) {
    var before = cartQuantities(beforeCart);
    var after = cartQuantities(afterCart);
    var added = [];
    var missing = [];
    requestItems.forEach(function (item) {
      var key = requestSignature(item);
      var delta = (after[key] || 0) - (before[key] || 0);
      var role = item.properties._ia_meal_bundle_role;
      if (delta >= item.quantity) added.push(role);
      else missing.push(role);
    });
    return { added: added, missing: missing };
  };

  MealController.prototype.add = function (mode) {
    var controller = this;
    if (this.pending) return;
    var state = this.update();
    if (mode === 'complete' && state.missing.length) return;
    var items = this.requestItems(mode);
    if (!items.length) return;

    var drawer = cartDrawer();
    var sections = cartSections(drawer);
    var addUrl = (window.routes && window.routes.cart_add_url) || (ROOT_URL + 'cart/add.js');
    var selectedValue = items.reduce(function (sum, item) {
      var selection = state.selected.find(function (candidate) { return candidate.variant.id === item.id; });
      return sum + (selection ? selection.price * item.quantity : 0);
    }, 0);

    dispatchAnalytics('ia_meal_bundle_add_started', {
      bundle_id: this.bundleId,
      bundle_name: this.bundleName,
      placement: this.placement,
      add_mode: mode,
      item_count: items.length,
      selected_value_cents: selectedValue,
      optional_count: state.selected.filter(function (selection) { return selection.item.dataset.behavior === 'optional'; }).length,
      subscription_count: this.subscriptionInput && this.subscriptionInput.checked ? items.length : 0
    });

    this.setPending(true);
    if (drawer && typeof drawer.setActiveElement === 'function') drawer.setActiveElement(document.activeElement);

    var beforeCart;
    fetchCart()
      .then(function (cart) {
        beforeCart = cart;
        return fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: items,
            sections: sections,
            sections_url: window.location.pathname
          })
        });
      })
      .then(parseJsonResponse)
      .then(function (result) {
        var response = result.response;
        var data = result.data;
        if (!response.ok || data.status) {
          var error = new Error(data.description || data.message || 'The selected groceries could not be added.');
          error.cartData = data;
          throw error;
        }
        return controller.renderSuccessfulCart(data, drawer, sections, items[0].id).then(function () {
          refreshMealCartSnapshot(false);
          controller.announce(items.length + ' groceries added to cart.');
          controller.showNotice('Added ' + items.length + ' selected groceries to your cart.', false);
          dispatchAnalytics('ia_meal_bundle_add_to_cart', {
            bundle_id: controller.bundleId,
            bundle_name: controller.bundleName,
            placement: controller.placement,
            status: 'complete',
            add_mode: mode,
            added_item_count: items.length,
            added_value_cents: selectedValue
          });
        });
      })
      .catch(function (error) {
        return fetchCart().then(function (afterCart) {
          var reconciled = controller.reconcile(beforeCart || { items: [] }, afterCart, items);
          if (reconciled.added.length) {
            return refreshDrawerSections(drawer, sections).catch(function () { return null; }).then(function () {
              var message = 'Added ' + reconciled.added.length + ' item' + (reconciled.added.length === 1 ? '' : 's') + '. Could not add: ' + reconciled.missing.join(', ') + '.';
              controller.showNotice(message, true);
              controller.announce(message);
              dispatchAnalytics('ia_meal_bundle_add_to_cart', {
                bundle_id: controller.bundleId,
                bundle_name: controller.bundleName,
                placement: controller.placement,
                status: 'partial',
                add_mode: mode,
                added_item_count: reconciled.added.length,
                missing_roles: reconciled.missing
              });
            });
          }
          controller.showNotice(error.message || 'The selected groceries could not be added.', true);
          controller.announce('The selected groceries could not be added.');
          dispatchAnalytics('ia_meal_bundle_add_failed', {
            bundle_id: controller.bundleId,
            bundle_name: controller.bundleName,
            placement: controller.placement,
            add_mode: mode,
            failure_type: 'cart_error',
            message: String(error.message || '').slice(0, 180)
          });
        }).catch(function () {
          controller.showNotice(error.message || 'The selected groceries could not be added.', true);
          controller.announce('The selected groceries could not be added.');
        });
      })
      .finally(function () {
        controller.setPending(false);
      });
  };

  var mealCartSnapshot = null;
  var mealCartUpdateTimer = null;
  var slideCartRenderTimers = [];

  function mealRecommendationForCart(cart) {
    if (!cart || !cart.items || !cart.items.length || toNumber(cart.total_price, 0) >= 10000) return null;
    var mealLine = cart.items.find(function (item) {
      return Boolean(privateProperty(item, '_ia_meal_bundle_id'));
    });
    if (!mealLine) return null;

    var mealId = privateProperty(mealLine, '_ia_meal_bundle_id');
    var gap = Math.max(0, 10000 - toNumber(cart.total_price, 0));
    var recommendation = {
      meal_id: mealId,
      gap_cents: gap,
      eyebrow: 'Reach the $100 order minimum',
      title: 'Add another meal for the week',
      copy: 'Choose another ready-to-cart meal and finish building this delivery.',
      url: '/pages/meals-made-easy',
      label: 'Explore Meals Made Easy',
      bundle_id: 'meals-made-easy'
    };

    if (gap <= 2000) {
      recommendation.title = 'Round out the delivery from the pantry';
      recommendation.copy = 'You are close to the order minimum. Add a useful pantry staple or two for the week.';
      recommendation.url = '/collections/pantry-staples';
      recommendation.label = 'Shop pantry staples';
      recommendation.bundle_id = 'cart-minimum-pantry';
      return recommendation;
    }

    var byMeal = {
      'family-pita-pizza-night': {
        title: 'Add breakfast for the week',
        copy: 'Pair Pizza Night with five make-ahead breakfast sandwiches and move past the order minimum.',
        url: '/pages/breakfast-sandwich-week',
        label: 'See Breakfast Sandwich Week',
        bundle_id: 'breakfast-sandwich-week'
      },
      'easy-sheet-pan-chicken-dinner': {
        title: 'Add dinner for two',
        copy: 'Pair Sheet-Pan Chicken with the Wild Salmon Dinner and finish building the week.',
        url: '/pages/wild-salmon-dinner',
        label: 'See Wild Salmon Dinner',
        bundle_id: 'wild-salmon-dinner'
      },
      'wild-salmon-dinner': {
        title: 'Add breakfast for the week',
        copy: 'Five make-ahead breakfast sandwiches are an easy way to complete this delivery.',
        url: '/pages/breakfast-sandwich-week',
        label: 'See Breakfast Sandwich Week',
        bundle_id: 'breakfast-sandwich-week'
      },
      'breakfast-sandwich-week': {
        title: 'Add Pizza Night',
        copy: 'Add an easy family dinner and finish building this delivery in one more step.',
        url: '/pages/family-pita-pizza-night',
        label: 'See Family Pita Pizza Night',
        bundle_id: 'family-pita-pizza-night'
      }
    };
    return Object.assign(recommendation, byMeal[mealId] || {});
  }

  function appendTextElement(parent, tag, className, text) {
    var element = document.createElement(tag);
    element.className = className;
    element.textContent = text;
    parent.appendChild(element);
    return element;
  }

  function renderSlideCartRecommendation(cart) {
    var hasMeal = Boolean(cart && cart.items && cart.items.some(function (item) {
      return Boolean(privateProperty(item, '_ia_meal_bundle_id'));
    }));
    var recommendation = mealRecommendationForCart(cart);
    document.body.classList.toggle('ia-mme-cart-has-meal', hasMeal);

    document.querySelectorAll('.ia-mme-slidecart-rec').forEach(function (node) { node.remove(); });
    document.querySelectorAll('.xb_slide_cart_upsell').forEach(function (node) {
      if (hasMeal) {
        node.setAttribute('aria-hidden', 'true');
        node.style.setProperty('display', 'none', 'important');
      } else {
        node.removeAttribute('aria-hidden');
        node.style.removeProperty('display');
      }
    });

    if (!recommendation) return false;
    var slideCartBody = document.querySelector('.xb_slidecart .xb_slide_cart_body_wrapper_inner');
    if (!slideCartBody) return false;

    var card = document.createElement('aside');
    card.className = 'ia-mme-slidecart-rec';
    card.setAttribute('aria-label', recommendation.eyebrow);
    appendTextElement(card, 'span', 'ia-mme-slidecart-rec__eyebrow', recommendation.eyebrow);
    appendTextElement(card, 'h3', 'ia-mme-slidecart-rec__title', recommendation.title);
    appendTextElement(card, 'p', 'ia-mme-slidecart-rec__copy', recommendation.copy);
    appendTextElement(card, 'strong', 'ia-mme-cart-rec__gap', formatMoney(recommendation.gap_cents) + ' to the order minimum');
    var link = appendTextElement(card, 'a', 'ia-mme-slidecart-rec__link', recommendation.label + ' →');
    link.href = recommendation.url;
    link.dataset.mmeBundleLink = '';
    link.dataset.bundleId = recommendation.bundle_id;
    link.dataset.placement = 'xboost_slidecart';
    link.addEventListener('click', function () {
      dispatchAnalytics('ia_meal_bundle_recommendation_click', {
        bundle_id: recommendation.bundle_id,
        placement: 'xboost_slidecart',
        minimum_gap_cents: recommendation.gap_cents
      });
    });
    slideCartBody.appendChild(card);
    return true;
  }

  function scheduleSlideCartRender(cart) {
    slideCartRenderTimers.forEach(function (timer) { window.clearTimeout(timer); });
    slideCartRenderTimers = [0, 350, 900, 1800].map(function (delay) {
      return window.setTimeout(function () { renderSlideCartRecommendation(cart); }, delay);
    });
  }

  function refreshMealCartSnapshot(trackRemovals) {
    return fetchCart().then(function (cart) {
      var next = mealCartLines(cart);
      if (trackRemovals && mealCartSnapshot) {
        Object.keys(mealCartSnapshot).forEach(function (key) {
          var before = mealCartSnapshot[key];
          var afterQuantity = next[key] ? next[key].quantity : 0;
          var removedQuantity = before.quantity - afterQuantity;
          if (removedQuantity <= 0) return;
          dispatchAnalytics('ia_meal_bundle_item_removed', {
            bundle_id: before.bundle_id,
            bundle_name: before.bundle_name,
            bundle_role: before.bundle_role,
            variant_id: before.variant_id,
            removed_quantity: removedQuantity,
            removed_value_cents: removedQuantity * before.unit_price_cents
          });
        });
      }
      mealCartSnapshot = next;
      scheduleSlideCartRender(cart);
      return cart;
    }).catch(function () { return null; });
  }

  function scheduleMealCartSnapshot() {
    if (mealCartUpdateTimer) window.clearTimeout(mealCartUpdateTimer);
    mealCartUpdateTimer = window.setTimeout(function () {
      mealCartUpdateTimer = null;
      refreshMealCartSnapshot(true);
    }, 300);
  }

  function initializeCartRemovalTracking() {
    if (window.__iaMmeRemovalTracking === true) return;
    window.__iaMmeRemovalTracking = true;
    refreshMealCartSnapshot(false);
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, scheduleMealCartSnapshot);
    }
    if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      document.addEventListener(window.PUB_SUB_EVENTS.cartUpdate, scheduleMealCartSnapshot);
    }
    document.addEventListener('click', function (event) {
      if (!event.target.closest('a[href="/cart"], a[href$="/cart"], #cart-icon-bubble, .header__icon--cart, [data-cart-drawer-open]')) return;
      window.setTimeout(function () {
        refreshMealCartSnapshot(false);
      }, 120);
    });
  }

  function initializeControllers() {
    initializeCartRemovalTracking();
    document.querySelectorAll('[data-mme-controller]').forEach(function (root) {
      if (root.dataset.mmeReady === 'true') return;
      root.dataset.mmeReady = 'true';
      new MealController(root);
    });

    document.querySelectorAll('[data-mme-bundle-link]').forEach(function (link) {
      if (link.dataset.mmeTracked === 'true') return;
      link.dataset.mmeTracked = 'true';
      link.addEventListener('click', function () {
        dispatchAnalytics('ia_meal_bundle_recommendation_click', {
          bundle_id: link.dataset.bundleId || '',
          placement: link.dataset.placement || 'landing'
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeControllers);
  } else {
    initializeControllers();
  }
})();
